import { useRef, useEffect, useState, useMemo } from 'react';

/**
 * HeatmapOverlay — a translucent heat layer on a canvas, meant to be stacked
 * over the live camera frame (see LiveStreamPlayer's `videoOverlay` prop).
 *
 * The AI module reports a coarse spatial grid (rows × cols) that covers the
 * whole frame, so this layer just fills its parent box — the parent is
 * responsible for matching the video's real content box.
 *
 * The heat field is computed at a small working resolution and stretched by
 * CSS: a Gaussian blur over a full-resolution frame costs hundreds of ms in JS
 * and looks identical once the browser smooths it.
 */

/** Canvas working width in px — height follows the box aspect. */
const WORK_W = 256;

/**
 * JET colormap approximation (blue → cyan → green → yellow → red).
 */
export function jetColormap(t: number): [number, number, number] {
  t = Math.max(0, Math.min(1, t));
  let r: number, g: number, b: number;
  if (t < 0.125) { r = 0; g = 0; b = 0.5 + t * 4; }
  else if (t < 0.375) { r = 0; g = (t - 0.125) * 4; b = 1; }
  else if (t < 0.625) { r = (t - 0.375) * 4; g = 1; b = 1 - (t - 0.375) * 4; }
  else if (t < 0.875) { r = 1; g = 1 - (t - 0.625) * 4; b = 0; }
  else { r = 1 - (t - 0.875) * 2; g = 0; b = 0; }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

/** CSS gradient stops for the legend, sampled from the same colormap. */
export const JET_GRADIENT = Array.from({ length: 9 }, (_, i) => {
  const [r, g, b] = jetColormap(i / 8);
  return `rgb(${r},${g},${b}) ${(i / 8) * 100}%`;
}).join(', ');

/**
 * Separable 2D Gaussian blur on a Float32Array.
 */
function gaussianBlur(data: Float32Array, width: number, height: number, sigma: number): Float32Array {
  if (sigma <= 0) return data;
  const radius = Math.ceil(sigma * 3);
  const kernel: number[] = [];
  let kernelSum = 0;
  for (let i = -radius; i <= radius; i++) {
    const val = Math.exp(-(i * i) / (2 * sigma * sigma));
    kernel.push(val);
    kernelSum += val;
  }
  for (let i = 0; i < kernel.length; i++) kernel[i] /= kernelSum;

  // Horizontal
  const temp = new Float32Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let sum = 0;
      for (let k = -radius; k <= radius; k++) {
        const sx = Math.min(width - 1, Math.max(0, x + k));
        sum += data[y * width + sx] * kernel[k + radius];
      }
      temp[y * width + x] = sum;
    }
  }
  // Vertical
  const result = new Float32Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let sum = 0;
      for (let k = -radius; k <= radius; k++) {
        const sy = Math.min(height - 1, Math.max(0, y + k));
        sum += temp[sy * width + x] * kernel[k + radius];
      }
      result[y * width + x] = sum;
    }
  }
  return result;
}

/**
 * Bilinear upscale of a 2D grid to canvas size.
 */
function bilinearUpscale(grid: number[][], rows: number, cols: number, width: number, height: number): Float32Array {
  const result = new Float32Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const gx = (x / width) * cols - 0.5;
      const gy = (y / height) * rows - 0.5;
      const x0 = Math.max(0, Math.floor(gx));
      const x1 = Math.min(cols - 1, x0 + 1);
      const y0 = Math.max(0, Math.floor(gy));
      const y1 = Math.min(rows - 1, y0 + 1);
      const fx = gx - x0;
      const fy = gy - y0;
      result[y * width + x] =
        grid[y0][x0] * (1 - fx) * (1 - fy) +
        grid[y0][x1] * fx * (1 - fy) +
        grid[y1][x0] * (1 - fx) * fy +
        grid[y1][x1] * fx * fy;
    }
  }
  return result;
}

export interface HeatmapOverlayProps {
  /** 2D array of hit counts (rows × cols) */
  spatialGrid: number[][] | null;
  /** Fallback row count when the grid is empty */
  gridRows?: number;
  /** Fallback col count when the grid is empty */
  gridCols?: number;
  /** Peak overlay opacity 0-1 (cold cells fade out below it) */
  opacity?: number;
  /** Gaussian sigma as a fraction of the box width */
  spread?: number;
  /** Draw the analytics grid lines */
  showGrid?: boolean;
  className?: string;
}

export default function HeatmapOverlay({
  spatialGrid,
  gridRows,
  gridCols,
  opacity = 0.6,
  spread = 0.07,
  showGrid = false,
  className = '',
}: HeatmapOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [aspect, setAspect] = useState(16 / 9);

  // Grid dimensions come from the data itself — the reported grid_rows/cols can
  // disagree with the array and indexing past its end throws.
  const rows = Array.isArray(spatialGrid) ? spatialGrid.length : 0;
  const cols = rows && Array.isArray(spatialGrid?.[0]) ? spatialGrid![0].length : 0;

  // Track the box we are stretched over, so the blur stays circular on screen.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w > 0 && h > 0) setAspect(w / h);
    });
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  // The blur is the expensive part — keep it out of the opacity path so
  // dragging the opacity slider only re-colorizes.
  const field = useMemo(() => {
    const w = WORK_W;
    const h = Math.max(1, Math.round(WORK_W / (aspect > 0 ? aspect : 16 / 9)));
    if (!rows || !cols || !spatialGrid) return { w, h, data: null as Float32Array | null, max: 0 };

    const blurred = gaussianBlur(
      bilinearUpscale(spatialGrid, rows, cols, w, h), w, h, Math.max(1, spread * w),
    );
    let max = 0;
    for (let i = 0; i < blurred.length; i++) {
      if (blurred[i] > max) max = blurred[i];
    }
    return { w, h, data: blurred as Float32Array | null, max };
  }, [spatialGrid, rows, cols, spread, aspect]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { w, h, data, max } = field;
    canvas.width = w;
    canvas.height = h;
    ctx.clearRect(0, 0, w, h);
    if (!data || max <= 0) return;

    // Alpha ramps with intensity so cold regions stay readable as video —
    // a flat alpha turns the whole frame into a blue wash.
    const image = ctx.createImageData(w, h);
    const px = image.data;
    for (let i = 0; i < w * h; i++) {
      const t = data[i] / max;
      if (t < 0.06) { px[i * 4 + 3] = 0; continue; }
      const [r, g, b] = jetColormap(t);
      px[i * 4] = r;
      px[i * 4 + 1] = g;
      px[i * 4 + 2] = b;
      px[i * 4 + 3] = Math.round(Math.min(1, Math.pow(t, 0.7)) * opacity * 255);
    }
    ctx.putImageData(image, 0, 0);
  }, [field, opacity]);

  const lineRows = rows || gridRows || 0;
  const lineCols = cols || gridCols || 0;

  return (
    <div className={`absolute inset-0 ${className}`}>
      <canvas ref={canvasRef} className="w-full h-full" />
      {showGrid && lineRows > 0 && lineCols > 0 && (
        // Vector lines rather than canvas pixels — the canvas is upscaled by CSS
        // and grid lines drawn into it would blur out.
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox={`0 0 ${lineCols} ${lineRows}`}
          preserveAspectRatio="none"
        >
          {Array.from({ length: lineCols - 1 }, (_, i) => (
            <line key={`v${i}`} x1={i + 1} y1={0} x2={i + 1} y2={lineRows}
              stroke="rgba(255,255,255,0.35)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
          ))}
          {Array.from({ length: lineRows - 1 }, (_, j) => (
            <line key={`h${j}`} x1={0} y1={j + 1} x2={lineCols} y2={j + 1}
              stroke="rgba(255,255,255,0.35)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
          ))}
        </svg>
      )}
    </div>
  );
}
